import debug from '@commons-shared/debug';
import { config } from '../config/environment';
import { post } from '../utils/api-router';
import { getRoutingConfig } from '../utils/routing-config-manager.js';

/**
 * Récupère le modèle IA pour un type spécifique depuis la configuration de routage
 * @param {string} iaType - Type d'IA: "text", "vision" ou "translation"
 * @returns {Promise<string|null>} - Le modèle IA ou null si non trouvé
 */
const getIAModel = async (iaType) => {
  try {
    const routingConfig = await getRoutingConfig();
    const section = routingConfig[iaType];
    
    if (!section || !section['X-Model']) {
      debug.warn(config, `[getIAModel] Aucun modèle trouvé pour ${iaType}`, section);
      return null;
    }
    
    debug.log(config, `[getIAModel] Modèle ${iaType}: ${section['X-Model']}`);
    return section['X-Model'];
  } catch (error) {
    debug.error(config, `[getIAModel] Erreur lors de la récupération du modèle ${iaType}:`, error);
    return null;
  }
};

/**
 * Appelle un service d'IA avec un tableau de messages formatés
 * @param {Array} theMessages - Tableau de structures Messages avec format:
 *                             { IA: "vision"|"text"|"translation"|"none", 
 *                               role: "system"|"user"|"assistant", 
 *                               temperature: Number,
 *                               text: String,
 *                               url: String }
 * @param {string} workingLanguage - Langue de travail pour l'IA (optionnel, défaut: 'FR')
 * @returns {Promise} - Promesse contenant la réponse de l'API
 */
export const callIA = async (theMessages, workingLanguage = 'FR') => {
  debug.log(config, `[callIA] Début de l'appel IA avec ${theMessages.length} messages`, { messagesCount: theMessages.length, workingLanguage });
  
  if (!theMessages || !Array.isArray(theMessages) || theMessages.length === 0) {
    debug.error(config, "[callIA] Le tableau de messages ne peut pas être vide");
    return Promise.reject(new Error("Le tableau de messages ne peut pas être vide"));
  }
 
  // 1. Extraire les informations et construire les messages
  let theIA = "none";
  let theTemperature = null;
  const MessageOrder = ["system", "user", "assistant"];
  const theMessagesInOrder = [];

  // Déterminer l'IA et la température à utiliser
  for (const messageType of MessageOrder) {
    for (let i = 0; i < theMessages.length; i++) {
      if (theMessages[i].role === messageType) {
        // Vérification des rôles valides
        if (!MessageOrder.includes(theMessages[i].role)) {
          debug.error(config, `[callIA] Role invalide: ${theMessages[i].role}`, theMessages[i]);
          return Promise.reject(new Error(`Role invalide: ${theMessages[i].role}`));
        }

        // Sélection de l'IA selon la priorité
        if ((theMessages[i].IA === "vision" || theMessages[i].IA === "translation" || theMessages[i].IA === "text")) {
          if (theMessages[i].IA === "vision") {
            theIA = "vision";
          } else if (theMessages[i].IA === "translation" && theIA !== "vision") {
            theIA = "translation";
          } else if (theMessages[i].IA === "text" && theIA !== "vision" && theIA !== "translation") {
            theIA = "text";
          }
        }

        // Sélection de la température
        if (theMessages[i].temperature !== undefined && 
            (theTemperature === null || theMessages[i].temperature > theTemperature)) {
          theTemperature = theMessages[i].temperature;
        }

        // Ajouter au tableau ordonné
        theMessagesInOrder.push({
          role: theMessages[i].role,
          text: theMessages[i].text,
          url: theMessages[i].url
        });
      }
    }
  }

  // 2. Contrôles
  if (theIA === "none") {
    debug.error(config, "[callIA] Aucun type d'IA valide n'a été spécifié", theMessages);
    return Promise.reject(new Error("Aucun type d'IA valide n'a été spécifié"));
  }

  if (theMessagesInOrder.length === 0) {
    debug.error(config, "[callIA] Aucun message valide n'a été trouvé", theMessages);
    return Promise.reject(new Error("Aucun message valide n'a été trouvé"));
  }

  // 3. Déterminer le modèle à utiliser selon le type d'IA
  let model;
  switch (theIA) {
    case "translation":
      model = await getIAModel("translation");
      break;
    case "vision":
      model = await getIAModel("vision");
      break;
    case "text":
      model = await getIAModel("text");
      break;
    default:
      debug.error(config, `[callIA] Type d'IA non pris en charge: ${theIA}`, theMessages);
      return Promise.reject(new Error(`Type d'IA non pris en charge: ${theIA}`));
  }

  if (!model) {
    debug.error(config, `[callIA] Aucun modèle défini pour le type d'IA: ${theIA}`, theMessages);
    return Promise.reject(new Error(`Aucun modèle défini pour le type d'IA: ${theIA}`));
  }

  // Construire le corps de la requête
  // Note: le modèle est désormais passé via le header X-Routing-Data (en-tête de requête)
  const requestBody = {
    temperature: theTemperature,
    working_language: workingLanguage,
    messages: theMessagesInOrder
  };

  debug.log(config, `[callIA] Appel API pour ${theIA} avec ${theMessagesInOrder.length} messages`, requestBody);

  // Effectuer l'appel API
  try {
    const responseData = await post(`/api/ia/${theIA}/run`, requestBody);
    debug.log(config, `[callIA] Réponse API réussie pour ${theIA}:`, responseData);
    return responseData;

  } catch (error) {
    debug.error(config, `[callIA] Erreur lors de l'appel API: ${error.message}`, error);
    throw error;
  }
};
